import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  PingResponse,
  PingRequest,
  OpenGraphData,
  UpdateHandoffResponse,
  FileData,
} from "../types/api";
import { RootState } from "../store";
import { HandoffOffer } from "../chat/types";
import { finishStreaming } from "../slices/chatSlice";
import { upsertConversation } from "../slices/conversationSlice";
import { selectChallengeHeaders, selectEffectiveLocale } from "./selectors";
import log from "../log";
import { getFrameStorage } from "../storage";

export function getUserLocalePreferences(): string[] {
  if ("languages" in navigator && navigator.languages.length > 0)
    return [...navigator.languages];
  else return [navigator.language];
}

export function getDeviceInformation() {
  return {
    locales: getUserLocalePreferences(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezone_offset: new Date().getTimezoneOffset(),
  };
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public headers: Record<string, string>,
    public body: any,
    message?: string
  ) {
    super(message || `Api Error ${status}`);

    this.name = "ApiError";

    // Restore prototype chain for instanceof checks
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  toJSON() {
    return {
      status: this.status,
      headers: this.headers,
      body: this.body,
      message: this.message,
    };
  }
}

type SerializedApiError = ReturnType<ApiError["toJSON"]>;

export async function pingFrameApi(
  request: PingRequest
): Promise<PingResponse> {
  const { frame_id, api_base } = request;

  const reqHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...(request.device_id && { "X-Device-Id": request.device_id }),
    ...(request.user_id && { "X-User-Id": request.user_id }),
    ...(request.session_id && { "X-Session-Id": request.session_id }),
    ...request.extra_headers,
  };

  const reqBody = JSON.stringify({
    user_data: request.user_data,
    locales: request.locales,
    locale: request.locale,
    timezone: request.timezone,
    timezone_offset: request.timezone_offset,
    browser_url: request.browser_url,
  });

  const response = await fetch(`${api_base}/v1/frames/${frame_id}/ping`, {
    method: "POST",
    headers: reqHeaders,
    body: reqBody,
  });

  // Always get the response data, regardless of status
  const responseData = await response.json().catch(() => null);
  const message =
    response.status == 429
      ? "Too many requests. Please try again later."
      : responseData?.message || `API Error: ${response.status}`;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      Object.fromEntries(response.headers.entries()),
      responseData,
      message
    );
  }

  return {
    data: responseData,
    headers: {
      device_id: request.device_id,
      user_id: response.headers.get("X-User-Id") || "",
      session_id: response.headers.get("X-Session-Id") || "",
    },
  };
}

export const pingFrame = createAsyncThunk<
  PingResponse,
  PingRequest,
  {
    state: RootState;
    rejectValue: SerializedApiError;
  }
>("shared/pingFrame", async (request, { rejectWithValue }) => {
  try {
    log.debug(request);
    return await pingFrameApi(request);
  } catch (error) {
    if (error instanceof ApiError) {
      console.error("[botbrains] API ERROR ", error.status, error.toJSON());
      return rejectWithValue(error.toJSON());
    }
    // TODO(liamvdv): handle status=429.
    // Create a normalized API error for unexpected errors
    const unexpectedError = new ApiError(
      500,
      {},
      { message: error instanceof Error ? error.message : "Unknown error" },
      "Unexpected error occurred"
    );
    return rejectWithValue(unexpectedError.toJSON());
  }
});

// silent defines whether the ping request should be silent (in background)
export function getPingRequestFromState(
  state: RootState,
  background: boolean = false
): PingRequest {
  const { apiBase, frameId } = state.config.data;

  // Ensure frameId is never null or empty when making ping requests
  if (!frameId || frameId.trim() === "") {
    throw new Error("frameId is required for ping requests");
  }

  const { user_data } = state.user.data;
  const { deviceId, userId, sessionId } = getFrameStorage(frameId);
  const { locales, timezone, timezone_offset } = getDeviceInformation();

  const request: PingRequest = {
    user_data: user_data,
    device_id: deviceId,
    user_id: userId || "",
    session_id: sessionId || "",
    locales: [...locales],
    // NOTE(liamvdv): First /ping has incorrect locale (en-US), because frame settings are not loaded yet.
    locale: state.config.data.locale ?? selectEffectiveLocale(state) ?? "en-US",
    timezone,
    timezone_offset,
    browser_url: window.location.href,
    frame_id: frameId,
    api_base: apiBase,
    background,
    extra_headers: selectChallengeHeaders(frameId)(state),
  };
  return request;
}

// background means the request is silent (not visible to the user, not updating .isLoading, .error and the result is ignored if not successful)
export const pingFrameWithCurrentState = createAsyncThunk<
  PingResponse, // Return type
  { refresh: boolean; background: boolean }, // Input argument type
  {
    state: RootState; // Typing getState for better autocomplete
    rejectValue: SerializedApiError;
  }
>(
  "shared/pingFrameWithCurrentState",
  async ({ background, refresh }, { dispatch, getState }) => {
    log.debug("Fetching data background", background, "refresh", refresh);
    const state = getState();
    const request = getPingRequestFromState(state, background);
    return dispatch(pingFrame(request)).unwrap();
  },
  {
    // Rate Limiting: Only allow one ping request at a time
    condition: ({ background, refresh }, { getState }) => {
      const state = getState();
      if (background) {
        return (
          (state.frame.backgroundStatus[state.config.data.frameId] ??
            "idle") !== "loading"
        );
      } else {
        // foreground
        const status = state.frame.status[state.config.data.frameId] ?? "idle";
        return status === "idle" || refresh;
      }
    },
  }
);

export const getOpenGraphData = createAsyncThunk<
  { url: string; data: OpenGraphData },
  string,
  {
    state: RootState;
    rejectValue: { message: string };
  }
>(
  "opengraph/getOpenGraphData",
  async (url, { getState, rejectWithValue }) => {
    const { apiBase } = getState().config.data;

    try {
      const response = await fetch(`${apiBase}/v1/opengraph?url=${url}`);
      const data = await response.json();
      return { url, data };
    } catch (err: any) {
      if (err instanceof Response) {
        return rejectWithValue({ message: await err.text() });
      }
      throw err;
    }
  },
  {
    condition: (url, { getState }) => {
      const status = getState().opengraph.status[url] || "idle";
      return status === "idle";
    },
  }
);

export const updateHandoff = createAsyncThunk<
  UpdateHandoffResponse, // Return type
  { handoff: HandoffOffer },
  {
    state: RootState; // Typing getState for better autocomplete
    rejectValue: any; // Typing rejectWithValue
  }
>("shared/updateHandoff", async (payload, { getState, rejectWithValue }) => {
  const { handoff } = payload;
  const state = getState();
  const { apiBase, frameId } = state.config.data;
  const ping = getPingRequestFromState(state);

  const reqHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...ping.extra_headers,
    ...(ping.device_id && { "X-Device-Id": ping.device_id }),
    ...(ping.user_id && { "X-User-Id": ping.user_id }),
    ...(ping.session_id && { "X-Session-Id": ping.session_id }),
  };

  const reqBody = JSON.stringify({
    id: handoff.id,
    message_id: handoff.message_id,
    status: handoff.status,
    subject: handoff.subject,
    body: handoff.body,
    // NOTE(liamvdv): api requires valid string or null, "" is invalid, thus turn cast to null.
    user_email: handoff.status == "SUBMITTED" ? handoff.user_email : null,
    ping: {
      user_data: ping.user_data,
      locales: ping.locales,
      locale: ping.locale,
      timezone: ping.timezone,
      timezone_offset: ping.timezone_offset,
      browser_url: ping.browser_url,
    },
  });

  try {
    const response = await fetch(
      `${apiBase}/v1/frames/${frameId}/handoffs/${handoff.id}`,
      {
        method: "PUT",
        headers: reqHeaders,
        body: reqBody,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return rejectWithValue({
        status: response.status,
        headers: Object.fromEntries(response.headers.entries()),
        body: errorData,
      });
    }

    const headers = {
      device_id: ping.device_id,
      user_id: response.headers.get("X-User-Id") || "",
      session_id: response.headers.get("X-Session-Id") || "",
    };

    // NOTE(liamvdv): 204 No Content
    // const data = await response.json();
    return { frameId, data: undefined, headers } as UpdateHandoffResponse;
  } catch (error) {
    if (error instanceof Response) {
      return rejectWithValue({
        status: error.status,
        headers: Object.fromEntries(error.headers.entries()),
        body: await error.json(),
      });
    }
    throw error;
  }
});

export const finishStreamingAndUpsertConversations = createAsyncThunk<
  void, // Return type (since we're not returning anything)
  { messageId: string },
  {
    state: RootState; // Typing getState for better autocomplete
    rejectValue: any; // Typing rejectWithValue
  }
>(
  "shared/finishStreamingAndUpdateConversations",
  async ({ messageId }, { dispatch, getState }) => {
    const state = getState();

    // First, finish streaming in chat slice
    dispatch(finishStreaming({ messageId }));

    // NOTE(liamvdv): optimistic UI update [emulates API retrival].
    const firstAssistantMessage = state.chat.messages.find(
      (m) => m.role === "assistant"
    );
    if (!state.chat.conversationId)
      throw new Error("assert conversation ID exists by now - logic error");
    dispatch(
      upsertConversation({
        conversation_id: state.chat.conversationId,
        created_at:
          firstAssistantMessage?.timestamp ?? new Date().toISOString(),
        messages: state.chat.messages.map((m) => ({
          ...m,
          isGenerating: false,
        })),
      })
    );
  }
);

// Async thunk for rating messages
export const rateMessage = createAsyncThunk<
  { messageId: string; rating: string },
  { messageId: string; rating: string },
  {
    state: RootState;
  }
>(
  "shared/rateMessage",
  async (
    {
      messageId,
      rating,
    }: {
      messageId: string;
      rating: string;
    },
    { getState }
  ) => {
    const state = getState();
    const { apiBase, frameId } = state.config.data;
    const conversationId = state.chat.conversationId;
    const payload = {
      conversation_id: conversationId,
      message_id: messageId,
      rating,
    };

    try {
      await fetch(`${apiBase}/v1/messages/${messageId}/rating`, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          ...selectChallengeHeaders(frameId)(state),
        }),
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error("[botbrains] Failed to rate message:", error);
      // Re-throw to maintain the async thunk error handling
      throw error;
    }

    return { messageId, rating };
  }
);

// Async thunk for rating conversations
export const rateConversation = createAsyncThunk<
  {
    conversationId: string;
    rating: number;
    comment?: string;
    conversation?: any;
  },
  { conversationId: string; rating: number; comment?: string },
  {
    state: RootState;
    rejectValue: { message: string };
  }
>(
  "shared/rateConversation",
  async (
    {
      conversationId,
      rating,
      comment = "",
    }: {
      conversationId: string;
      rating: number;
      comment?: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const { apiBase, frameId } = state.config.data;
      const pingRequestData = getPingRequestFromState(state);

      const payload = {
        score: rating,
        comment: comment || "",
        ping: {
          user_data: pingRequestData.user_data,
          locales: pingRequestData.locales,
          locale: selectEffectiveLocale(state),
          timezone: pingRequestData.timezone,
          timezone_offset: pingRequestData.timezone_offset,
          browser_url: window.location.href,
        },
      };

      const response = await fetch(
        `${apiBase}/v1/frames/${frameId}/conversations/${conversationId}/rating`,
        {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            ...selectChallengeHeaders(frameId)(state),
            ...(pingRequestData.device_id && {
              "X-Device-Id": pingRequestData.device_id,
            }),
            ...(pingRequestData.user_id && {
              "X-User-Id": pingRequestData.user_id,
            }),
            ...(pingRequestData.session_id && {
              "X-Session-Id": pingRequestData.session_id,
            }),
          }),
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          response.status,
          Object.fromEntries(response.headers.entries()),
          errorData,
          `Failed to submit rating: ${response.status}`
        );
      }

      // Handle new JSON response format {conversation: Conversation}
      const responseData = await response.json();
      return {
        conversationId,
        rating,
        comment,
        conversation: responseData.conversation,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue({ message: error.message });
      }
      return rejectWithValue({ message: "Failed to submit rating" });
    }
  }
);

export const uploadFile = createAsyncThunk<
  FileData,
  {
    file: File;
    type: "image" | "audio" | "document";
    signal?: AbortSignal;
  },
  {
    state: RootState;
    rejectValue: { message: string };
  }
>(
  "files/uploadFile",
  async ({ file, type, signal }, { getState, rejectWithValue }) => {
    const { apiBase } = getState().config.data;
    const frameId = getState().config.data.frameId;

    if (!frameId) {
      return rejectWithValue({ message: "Frame ID is not available" });
    }

    // Create the form data
    const formData = new FormData();
    formData.append("file", file);

    // Add metadata as separate form fields
    formData.append("name", file.name);
    formData.append("content_type", file.type);
    formData.append("type", type);
    formData.append("bytesize", file.size.toString());

    try {
      const response = await fetch(`${apiBase}/v1/frames/${frameId}/files`, {
        method: "POST",
        body: formData,
        signal, // Pass the AbortSignal to the fetch request
      });

      if (!response.ok) {
        return rejectWithValue({ message: await response.text() });
      }

      const data = await response.json();
      return data;
    } catch (err: any) {
      // Check if the error is an AbortError
      if (err.name === "AbortError") {
        throw err; // Rethrow AbortError to be handled by the caller
      }

      if (err instanceof Response) {
        return rejectWithValue({ message: await err.text() });
      }
      return rejectWithValue({
        message: err.message || "An unknown error occurred",
      });
    }
  }
);

export const deleteFile = createAsyncThunk<
  void,
  { fileId: string },
  {
    state: RootState;
    rejectValue: { message: string };
  }
>("files/deleteFile", async ({ fileId }, { getState, rejectWithValue }) => {
  const { apiBase } = getState().config.data;
  const frameId = getState().config.data.frameId;

  if (!frameId) {
    return rejectWithValue({ message: "Frame ID is not available" });
  }

  try {
    const response = await fetch(
      `${apiBase}/v1/frames/${frameId}/files/${fileId}`,
      {
        method: "DELETE",
      }
    );

    if (!response.ok) {
      return rejectWithValue({ message: await response.text() });
    }

    return;
  } catch (err: any) {
    if (err instanceof Response) {
      return rejectWithValue({ message: await err.text() });
    }
    return rejectWithValue({
      message: err.message || "An unknown error occurred",
    });
  }
});
